import get from "lodash/get"
import forEach from "lodash/forEach"
import toPairs from "lodash/toPairs"
import uniq from "lodash/uniq"
import map from "lodash/map"
import toString from "lodash/toString"
import isEmpty from "lodash/isEmpty"
import filter from "lodash/filter"
import includes from "lodash/includes"
import { userType } from "constants-lib/authentication"
import {
  USER_STATE_CONDITION_MAP,
  USER_PRODUCTS_MAP,
  INVALID_USER_LOBS,
} from "./constants"

/**
 * Returns an array of available LOBs for returning users based on their product selections.
 *
 * @function
 * @param {Object} customerSelections - The object that contains the user's selections.
 * @returns {Array} An array of available LOBs for returning users.
 *
 * The `getLOBsFromAvailableProducts` function takes an object that contains the user's selections and returns an array of available LOBs for returning users based on their product selections.
 * The function filters for only LOB keys from available products to return an array of available LOBs for returning users.
 * The function uses the `USER_PRODUCTS_MAP` object to map the LOB keys to LOB names.
 */
export const getLOBsFromAvailableProducts = (customerSelections) => {
  const userProducts = get(customerSelections, `userProducts`, {})
  const userLobs = []

  forEach(toPairs(userProducts), ([key, value]) => {
    if (value) {
      userLobs.push(USER_PRODUCTS_MAP[key])
    }
  })

  return userLobs
}

/**
 * Returns an array of LOBs for a customer account based on the get-me-started API response.
 *
 * @function
 * @param {Object} customerSelections - The object that contains the customer's selections.
 * @returns {Array} An array of LOBs for the customer account.
 *
 * The `getLOBsFromSearch` function takes an object that contains the customer's selections and returns an array of LOBs for the customer account based on the get-me-started API response.
 * The function extracts the customer accounts from the `combinedProductSupportData.customers` object and returns an array of LOBs for each account.
 * The function uses the `line_of_business` property of each account to determine the LOB.
 */
export const getLOBsFromSearch = (customerSelections) => {
  const customerAccounts = get(
    customerSelections,
    `combinedProductSupportData.customers`,
    {},
  )
  const userLobs = []

  forEach(customerAccounts, (account) => {
    const lob = get(account, `line_of_business`, ``).toUpperCase()
    userLobs.push(lob)
  })

  return userLobs
}

/**
 * Returns an array of unique LOBs from the linked accounts of the user's manage account object.
 *
 * @function
 * @param {Object} userManageAccount - The user's manage account object.
 * @returns {Array} An array of unique LOBs from the linked accounts of the user's manage account object.
 *
 * The `getLOBsFromLinkedAccounts` function takes a user's manage account object and returns an array of unique LOBs from the linked accounts of the object.
 * The function uses the `linkedAccounts` property of the object to extract the LOBs and returns an array of unique LOBs.
 */
export const getLOBsFromLinkedAccounts = (userManageAccount) =>
  uniq(
    map(get(userManageAccount, `linkedAccounts`, []), (account) =>
      get(account, `lob`, ``).toUpperCase(),
    ),
  )

/**
 * Returns the user state from local storage.
 *
 * @function
 * @returns {string} The user state from local storage.
 */
export const getUserStateFromLocalStorage = () =>
  localStorage.getItem(`userState`)

/**
 * Determines whether the user state is `EXISTING`.
 *
 * @function
 * @param {string} userState - The user state.
 * @returns {boolean} A boolean value indicating whether the user state is `EXISTING`.
 *
 */
export const isExistingUser = (userState) =>
  userState === toString(userType.EXISTING)

/**
 * Determines whether the user state is `COOKIED`.
 *
 * @function
 * @param {string} userState - The user state.
 * @returns {boolean} A boolean value indicating whether the user state is `COOKIED`.
 *
 */
export const isCookiedUser = (userState) =>
  userState === toString(userType.COOKIED)

/**
 * Determines whether the user state is `LOGGED_IN`.
 *
 * @function
 * @param {string} userState - The user state.
 * @returns {boolean} A boolean value indicating whether the user state is `LOGGED_IN`.
 */
export const isLoggedInUser = (userState) =>
  userState === toString(userType.LOGGED_IN)

/**
 * Returns an array of LOBs based on the user's state and selections.
 *
 * @function
 * @param {Object} customerSelections - The object that contains the customer's selections.
 * @param {Object} userManageAccount - The user's manage account data.
 * @param {string} [userState=getUserStateFromLocalStorage()] - The user state (ANONYMOUS, COOKIED, LOGGED_IN).
 * @returns {Array} An array of LOBs based on the user's state and selections.
 *
 * The `getLobs` function takes an object that contains the customer's selections, the user's manage account data, and an optional user state, and returns an array of LOBs based on the user's state and selections.
 * The function determines the user's state and uses the appropriate function to extract the LOBs from the user's selections or manage account data.
 * If the user state is not provided, the function retrieves it from local storage.
 * The function returns an array of unique LOBs sorted in alphabetical order.
 */
export const getLobs = (
  customerSelections,
  userManageAccount,
  userState = getUserStateFromLocalStorage(),
) => {
  if (!userState) {
    /* default to ANONYMOUS state if no userState is detected */
    // eslint-disable-next-line no-param-reassign
    userState = userType.ANONYMOUS.toString()
  }

  let lobs = []
  if (isExistingUser(userState)) {
    lobs = getLOBsFromSearch(customerSelections)
  } else if (isCookiedUser(userState)) {
    lobs = getLOBsFromAvailableProducts(customerSelections)
    if (isEmpty(lobs)) {
      /* if there are no found customer products, use search results */
      lobs = getLOBsFromSearch(customerSelections)
    }
  } else if (isLoggedInUser(userState)) {
    lobs = getLOBsFromLinkedAccounts(userManageAccount)
  }
  return lobs.sort()
}

/**
 * Returns the user state based on the user's selections and manage account data.
 *
 * @function
 * @param {Object} customerSelections - The object that contains the user's selected LOBs.
 * @param {string} userManageAccount - The user's account number.
 * @returns {string} The user state based on the user's selections and manage account data.
 *
 * The `getUserState` function takes an object that contains the user's selected LOBs and the user's account number, and returns the user state based on the user's selections and manage account data.
 * The function retrieves the user state from local storage and uses the `getLobs` function to extract the LOBs from the user's selections and manage account data.
 * The function filters out any invalid LOBs and sorts the remaining LOBs in alphabetical order.
 * The function then concatenates the user state and LOBs to form a condition, and uses the `USER_STATE_CONDITION_MAP` object to map the condition to a user state.
 * The function returns the user state.
 */
export const getUserState = (customerSelections, userManageAccount) => {
  const userState = getUserStateFromLocalStorage()

  /* sort userStates as order is not be guaranteed, we also want to filter out
   any invalid LOBs such as "NA" which is returned when linking a new account */
  const sortedLobs = filter(
    getLobs(customerSelections, userManageAccount, userState),
    (lob) => !includes(INVALID_USER_LOBS, lob),
  )

  const condition = `${userState}${sortedLobs}`

  return USER_STATE_CONDITION_MAP[condition]
}
